import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import Layout from '@/components/Layout';
import SEO from '@/components/SEO';
import LoadingSpinner from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { PAYMENTS_KEYS } from '@/hooks/usePayments';

type ConfirmingState = 'confirming' | 'success' | 'failed' | 'timeout' | 'canceled';

type FinalResult = {
  state: ConfirmingState;
  message?: string;
  invoiceUrl?: string | null;
};

function normalizePaymentStatus(value: unknown): 'SUCCESS' | 'FAILED' | 'PENDING' {
  const raw = String(value || '').trim().toUpperCase();
  if (raw === 'SUCCESS' || raw === 'SUCCEEDED' || raw === 'PAID' || raw === 'COMPLETED') return 'SUCCESS';
  if (raw === 'FAILED' || raw === 'FAIL' || raw === 'ERROR' || raw === 'CANCELLED' || raw === 'CANCELED') return 'FAILED';
  return 'PENDING';
}

export default function PaymentConfirming() {
  const [, setLocation] = useLocation();
  const [result, setResult] = useState<FinalResult>({
    state: 'confirming',
    message: 'Still confirming your payment...',
  });

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const paymentId = params.get('payment_id');
  const presetStatus = params.get('status');
  const presetMessage = params.get('message');

  useEffect(() => {
    if (!paymentId && presetStatus !== 'failed') {
      setResult({ state: 'failed', message: 'Payment ID is missing. Please try checkout again.' });
      return;
    }

    if (presetStatus === 'failed') {
      setResult({ state: 'failed', message: presetMessage || 'Payment failed.' });
      return;
    }

    let cancelled = false;

    const refreshAccountData = () => {
      queryClient.invalidateQueries({ queryKey: PAYMENTS_KEYS.current });
      queryClient.invalidateQueries({ queryKey: PAYMENTS_KEYS.history });
      queryClient.invalidateQueries({ queryKey: PAYMENTS_KEYS.methods });
      queryClient.invalidateQueries({ queryKey: ['/api/credits/balance'] });
      queryClient.invalidateQueries({ queryKey: ['/api/credits/summary'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user/profile'] });
    };

    const pollFallbackStatus = async (): Promise<FinalResult> => {
      for (let attempt = 0; attempt < 40; attempt++) {
        try {
          const response = await apiRequest('GET', `/api/payments/status/${paymentId}`);
          const data = await response.json();
          const status = normalizePaymentStatus(data?.status);

          if (status === 'SUCCESS') {
            return { state: 'success', message: data?.message || 'Payment successful.' };
          }

          if (status === 'FAILED') {
            return { state: 'failed', message: data?.message || 'Payment failed.' };
          }
        } catch {
          // Continue polling through transient backend/network failures.
        }

        await new Promise((resolve) => window.setTimeout(resolve, 2000));
      }

      return { state: 'timeout', message: 'Still confirming your payment...' };
    };

    const run = async () => {
      setResult({ state: 'confirming', message: 'Still confirming your payment...' });

      try {
        const response = await apiRequest('GET', `/api/payments/transactions/${paymentId}/wait?timeout=60&poll_interval=1.5&include_invoice=true`);
        const data = await response.json();
        const status = normalizePaymentStatus(data?.status);

        if (cancelled) return;

        if (status === 'SUCCESS') {
          refreshAccountData();
          setResult({
            state: 'success',
            message: data?.message || 'Payment successful.',
            invoiceUrl: data?.invoice_public_url || data?.invoice_presigned_url || null,
          });
          return;
        }

        if (status === 'FAILED') {
          setResult({ state: 'failed', message: data?.message || 'Payment failed.' });
          return;
        }

        const fallback = await pollFallbackStatus();
        if (cancelled) return;
        if (fallback.state === 'success') refreshAccountData();
        setResult(fallback);
      } catch {
        if (cancelled) return;
        const fallback = await pollFallbackStatus();
        if (cancelled) return;
        if (fallback.state === 'success') refreshAccountData();
        setResult(fallback);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [paymentId, presetMessage, presetStatus]);

  return (
    <Layout>
      <SEO
        title="Payment Confirmation | TradeSagePro"
        description="Confirming your payment and syncing your account status."
        canonical="https://tradesagepro.com/payments/confirming"
        noindex
        nofollow
      />
      <div className="min-h-screen bg-[#10151D] px-4 py-12 text-[hsl(210,9%,91%)]">
        <div className="mx-auto flex min-h-[70vh] max-w-xl items-center justify-center">
          <div className="w-full rounded-3xl border border-[#1C2A44] bg-[hsl(215,15%,14%)] p-8 shadow-[0_0_40px_rgba(12,18,28,0.6)]">
            <div className="space-y-4 text-center">
              {result.state === 'confirming' && (
                <>
                  <div className="flex justify-center">
                    <LoadingSpinner className="h-8 w-8" />
                  </div>
                  <h1 className="text-2xl font-bold text-white">Confirming payment</h1>
                  <p className="text-sm text-gray-300">{result.message || 'Still confirming your payment...'}</p>
                </>
              )}

              {result.state === 'success' && (
                <>
                  <h1 className="text-2xl font-bold text-emerald-400">Payment successful</h1>
                  <p className="text-sm text-gray-300">{result.message || 'Your payment was confirmed successfully.'}</p>
                  <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-center">
                    <Button onClick={() => setLocation('/dashboard')} className="bg-[#00FF8C] text-black hover:brightness-105">
                      Go to Dashboard
                    </Button>
                    <Button variant="ghost" onClick={() => setLocation('/pricing')} className="border border-white/20 text-white hover:bg-white/10">
                      Back to Pricing
                    </Button>
                  </div>
                  {result.invoiceUrl ? (
                    <div>
                      <a className="text-sm text-[hsl(193,100%,50%)] underline" href={result.invoiceUrl} target="_blank" rel="noreferrer">
                        View invoice
                      </a>
                    </div>
                  ) : null}
                </>
              )}

              {result.state === 'failed' && (
                <>
                  <h1 className="text-2xl font-bold text-red-400">Payment failed</h1>
                  <p className="text-sm text-gray-300">{result.message || 'The payment could not be completed.'}</p>
                  <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-center">
                    <Button onClick={() => setLocation('/pricing')} className="bg-[#4C73FF] text-white hover:brightness-110">
                      Try again
                    </Button>
                    <Button variant="ghost" onClick={() => setLocation('/profile')} className="border border-white/20 text-white hover:bg-white/10">
                      Go to Profile
                    </Button>
                  </div>
                </>
              )}

              {result.state === 'timeout' && (
                <>
                  <h1 className="text-2xl font-bold text-amber-300">Still processing</h1>
                  <p className="text-sm text-gray-300">{result.message || 'Still confirming your payment...'}</p>
                  <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-center">
                    <Button onClick={() => window.location.reload()} className="bg-[#4C73FF] text-white hover:brightness-110">
                      Retry check
                    </Button>
                    <Button variant="ghost" onClick={() => setLocation('/dashboard')} className="border border-white/20 text-white hover:bg-white/10">
                      Go to Dashboard
                    </Button>
                  </div>
                </>
              )}

              {result.state === 'canceled' && (
                <>
                  <h1 className="text-2xl font-bold text-white">Checkout closed</h1>
                  <p className="text-sm text-gray-300">The payment was not completed.</p>
                  <div className="pt-2">
                    <Button onClick={() => setLocation('/pricing')} className="bg-[#4C73FF] text-white hover:brightness-110">
                      Back to Pricing
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}